import { useCallback, useEffect, useRef, useState } from 'react';
import { applyLyrraA11yPreset } from '@/lib/lyrra-a11y-settings';
import {
  isLyrraAccessPadActivityKey,
  isLyrraAccessPadF13,
  LYRRA_ACCESS_PAD_CONNECTED_EVENT,
  LYRRA_ACCESS_PAD_DISCONNECTED_EVENT,
  LYRRA_ACCESS_PAD_F13_DOUBLE_MS,
  LYRRA_ACCESS_PAD_F13_TOGGLE_GUIDE_EVENT,
  LYRRA_ACCESS_PAD_IDLE_MS,
} from '@/lib/lyrra-access-pad';

/**
 * Détecte le Lyrra Access Pad :
 * - F13 (badge masqué) → affiche la notification + préréglage malvoyance ;
 * - F13 (badge visible, simple) → lecture / pause du guide vocal ;
 * - F13 ×2 rapide (badge visible) → quitte LyrraPad + préréglage standard ;
 * - autres touches Lyrra → maintien de session.
 */
export function useLyrraAccessPad() {
  const [connected, setConnected] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const f13SingleTapRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectedRef = useRef(false);

  const clearIdleTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const clearF13SingleTapTimer = useCallback(() => {
    if (f13SingleTapRef.current) {
      clearTimeout(f13SingleTapRef.current);
      f13SingleTapRef.current = null;
    }
  }, []);

  const disconnectPad = useCallback(
    (options?: { restoreStandardPreset?: boolean }) => {
      if (!connectedRef.current) return;
      clearF13SingleTapTimer();
      clearIdleTimer();
      connectedRef.current = false;
      setConnected(false);
      if (options?.restoreStandardPreset !== false) {
        applyLyrraA11yPreset('standard');
      }
      window.dispatchEvent(new CustomEvent(LYRRA_ACCESS_PAD_DISCONNECTED_EVENT));
    },
    [clearF13SingleTapTimer, clearIdleTimer],
  );

  const scheduleIdleDisconnect = useCallback(() => {
    clearIdleTimer();
    timeoutRef.current = setTimeout(() => {
      disconnectPad({ restoreStandardPreset: false });
    }, LYRRA_ACCESS_PAD_IDLE_MS);
  }, [clearIdleTimer, disconnectPad]);

  const markConnected = useCallback(() => {
    const wasConnected = connectedRef.current;
    connectedRef.current = true;
    setConnected(true);
    scheduleIdleDisconnect();
    if (!wasConnected) {
      applyLyrraA11yPreset('malvoyance');
      window.dispatchEvent(new CustomEvent(LYRRA_ACCESS_PAD_CONNECTED_EVENT));
    }
  }, [scheduleIdleDisconnect]);

  const handleF13WhileConnected = useCallback(() => {
    if (f13SingleTapRef.current) {
      clearF13SingleTapTimer();
      disconnectPad();
      return;
    }

    scheduleIdleDisconnect();
    f13SingleTapRef.current = setTimeout(() => {
      f13SingleTapRef.current = null;
      if (!connectedRef.current) return;
      window.dispatchEvent(new CustomEvent(LYRRA_ACCESS_PAD_F13_TOGGLE_GUIDE_EVENT));
    }, LYRRA_ACCESS_PAD_F13_DOUBLE_MS);
  }, [clearF13SingleTapTimer, disconnectPad, scheduleIdleDisconnect]);

  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      if (isLyrraAccessPadF13(event)) {
        if (event.repeat) return;
        if (connectedRef.current) {
          handleF13WhileConnected();
        } else {
          markConnected();
        }
        return;
      }

      if (event.repeat) return;

      if (connectedRef.current && isLyrraAccessPadActivityKey(event)) {
        scheduleIdleDisconnect();
      }
    };

    window.addEventListener('keydown', handleKeydown, true);
    return () => {
      window.removeEventListener('keydown', handleKeydown, true);
      clearIdleTimer();
      clearF13SingleTapTimer();
    };
  }, [
    clearF13SingleTapTimer,
    clearIdleTimer,
    handleF13WhileConnected,
    markConnected,
    scheduleIdleDisconnect,
  ]);

  return { connected };
}
